import React, { useMemo, useState, useEffect } from 'react';
import { useIntl } from 'umi';
import moment from 'moment';
import { useDebounceFn } from 'ahooks';
import { Segment, Justify, SearchBox } from 'tea-component';
import Loading from '@intelligence/components/Loading';
import ReportCard from '@intelligence/components/ReportCard';
import { fetchReportExcel } from '@intelligence/services/common';
import { getReportByIdApi } from '@intelligence/services/reportService';
import { isEmpty } from '@intelligence/v3/utils/common';
import { formatDateStr } from '@intelligence/v3/utils/date';
import { reportButtonLog } from '@intelligence/v3/utils/logReporter';
import { dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import { splitReportTagStr } from '@intelligence/v3/utils/topcharts';
import { languageItem } from '@intelligence/pages/MobileDevice/Reports/_utils';
import { LANGUAGE_CN } from '@home/_config/config';
import { DEBOUNCE_CONFIG } from '@intelligence/v3/_config/utils';
import { DATABRAIN_COS_ACCELERATE, DATABRAIN_COS_DOMAIN } from '@intelligence/_config/config';

import type { ICasualGamesReportsCommonProps } from '../../types';
import type { IReportItem, IProcessedReportItem } from './types';

import '@intelligence/assets/scss/BusinessReports/index.scss';
import '../index.scss';

const downloadExcelLogConfig = dbiBtnLogMes.casualGamesReportsMarketReportsDownloadExcel;

const processReportData = (reports?: IReportItem[] | null): IProcessedReportItem[] => {
  if (isEmpty(reports)) {
    return [];
  }

  return reports!.map((item) => {
    const coverImg = item?.cover?.replace(DATABRAIN_COS_DOMAIN, DATABRAIN_COS_ACCELERATE) || '';
    const itemTitle = item?.title || '';
    const itemTitleEN = item?.title_en || '';
    const itemTag = splitReportTagStr(item?.tag);
    const itemTagEN = splitReportTagStr(item?.tag_en);

    return {
      ...item,
      key: item?.resource_id || '',
      coverImg,
      title: [itemTitle, itemTitleEN],
      originTitle: [itemTitle, itemTitleEN],
      report_type: [item?.report_type || '', item?.report_type_en || ''],
      report_language: [item?.report_language || '', item?.report_language_en || ''],
      summary: [item?.introduction || '', item?.introduction_en || ''],
      tag: [itemTag, itemTagEN],
      time: formatDateStr(item?.create_time),
    };
  });
};

interface IMarketReportsTabProps extends ICasualGamesReportsCommonProps {}

const MarketReportsTab: React.FC<IMarketReportsTabProps> = (props) => {
  const { loading: loadingProps, reportIds } = props;

  const { locale, formatMessage } = useIntl();

  const [loading, setLoading] = useState(true);
  const [activeLanguage, setActiveLanguage] = useState('All');
  const [searchValue, setSearchValue] = useState('');
  const [allReports, setAllReports] = useState<IProcessedReportItem[]>([]);

  const lang = useMemo(() => (locale === LANGUAGE_CN ? 0 : 1), [locale]);

  const languageOptions = useMemo(() => {
    const languages = new Map<string, { value: string; text: string }>();

    allReports?.forEach((report) => {
      const langEn = report?.report_language?.[1];
      if (langEn && !languages.has(langEn)) {
        languages.set(langEn, { value: langEn, text: report?.report_language?.[lang] || langEn });
      }
    });

    return [{ value: 'All', text: lang === 0 ? '全部' : 'All' }, ...Array.from(languages.values())];
  }, [lang, allReports]);

  const handleSearch = (value: string) => {
    const trimmedValue = value?.trim() || '';
    if (!trimmedValue) {
      return;
    }

    const lowercasedValue = trimmedValue.toLowerCase();
    const globalSearchResults = allReports?.filter((report) => {
      return !!report?.title?.[lang]?.toLowerCase()?.includes(lowercasedValue);
    });

    if (!globalSearchResults?.length) {
      return;
    }

    const currentCategoryHasResults = globalSearchResults?.some((report) => {
      return activeLanguage === 'All' || report?.report_language?.[1] === activeLanguage;
    });

    if (!currentCategoryHasResults) {
      for (const option of languageOptions) {
        if (option.value === 'All') {
          continue;
        }

        const nextCategoryHasResults = globalSearchResults?.some((report) => {
          return report?.report_language?.[1] === option?.value;
        });

        if (nextCategoryHasResults) {
          setActiveLanguage(option.value);
          break;
        }
      }
    }
  };

  const { run: debouncedSearch } = useDebounceFn(handleSearch, DEBOUNCE_CONFIG);

  const onSearchChange = (value: string) => {
    setSearchValue(value);
    debouncedSearch(value);
  };

  const filteredReports = useMemo(() => {
    if (isEmpty(allReports)) {
      return [];
    }

    let reports = [...allReports];

    if (activeLanguage !== 'All') {
      reports = reports.filter((report) => {
        return report?.report_language?.[1] === activeLanguage;
      });
    }

    if (!!searchValue?.trim()) {
      const lowercasedValue = searchValue.trim().toLowerCase();
      reports = reports?.filter((report) => {
        return !!report?.title?.[lang]?.toLowerCase()?.includes(lowercasedValue);
      });
    }

    return reports;
  }, [lang, activeLanguage, searchValue, allReports]);

  useEffect(() => {
    if (loadingProps) {
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      try {
        const res = await getReportByIdApi({ resource_ids: reportIds });
        const processed = processReportData(res?.resource_list);
        setAllReports(processed);
      } catch (error) {
        console.error('Failed to fetch report data:', error);
      } finally {
        setLoading(false);
      }
    };

    void fetchData();
  }, [loadingProps, reportIds]);

  const handleDownloadRun = async (id: string, name: string) => {
    if (!id) {
      return;
    }
    reportButtonLog({
      ...downloadExcelLogConfig,
      extInfo: `Casual Games Hub / Reports / Market Reports页面下载Excel(${id})`,
    });
    const res = await fetchReportExcel({ id, name });
    const blob = new Blob([res]);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = URL.createObjectURL(blob);
    a.download = name;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    return true;
  };

  const renderReportListElement = () => {
    if (isEmpty(filteredReports)) {
      return <div className="dbi-casual-report-placeholder">{formatMessage({ id: 'dbi_reports_search_na_data' })}</div>;
    }

    return filteredReports!.map((item) => {
      return (
        <ReportCard
          hasPDF
          key={item?.resource_id}
          type="Market"
          systemId={item?.system}
          isNew={moment(item?.time)?.isAfter(moment()?.subtract(1, 'month'))}
          reportLangSegment={activeLanguage}
          data={languageItem(item, lang)}
          handleDownloadRun={handleDownloadRun}
        />
      );
    });
  };

  if (loading) {
    return <Loading content={'Loading...'} />;
  }

  return (
    <div className="dbi-businessreports dbi-market-report-full">
      <Justify
        className="dbi-businessreports-justify"
        left={
          <Segment
            className="segment-light"
            value={activeLanguage}
            options={languageOptions}
            onChange={(value: string) => setActiveLanguage(value)}
          />
        }
        right={
          <SearchBox
            placeholder={formatMessage({ id: 'dbi_reports_search_placeholder' })}
            value={searchValue}
            onChange={onSearchChange}
          />
        }
      />
      <div style={{ marginTop: '16px' }}>{renderReportListElement()}</div>
    </div>
  );
};

export default MarketReportsTab;
